// src/routes/$locale/collections/$networkSlug.tsx — param route for /collections/:networkSlug
// Numeric slugs (e.g. 14) redirect to chainName slug (e.g. flr-mainnet); string slugs pass through.
import { createFileRoute, redirect, Outlet } from '@tanstack/react-router'
import { getCollections } from '@/lib/api'
import CollectionsContainer from '@/containers/Collections'
import { createItemListSchema } from '@/utils/metadata'
import { setPageMetadata } from '@/utils/page-metadata'
import { t } from '@/i18n/translator'
import chainData from '@/chains'

const chainNameSlugToChainIdMap: Record<string, string> = {}
const chainIdToChainNameSlugMap: Record<string, string> = {}
for (const entry of chainData) {
  if (entry.chainName) {
    const slug = entry.chainName.toLowerCase().replace(/\s+/g, '-')
    chainNameSlugToChainIdMap[slug] = String(entry.chainId)
    chainIdToChainNameSlugMap[String(entry.chainId)] = slug
  }
}

export const Route = createFileRoute('/$locale/collections/$networkSlug/')({
  // SSR prefetched data is treated as fresh forever so the client NEVER
  // re-runs these loaders on hydration. NOTE: must NOT use `Infinity` —
  // JSON.stringify(Infinity) === null, which TanStack treats as staleTime:0
  // and re-fetches. Number.MAX_SAFE_INTEGER serializes correctly and is
  // functionally "never stale" (~285,000 years).
  staleTime: Number.MAX_SAFE_INTEGER,
  loader: async ({ params: { networkSlug }, location }) => {
    // TanStack Router re-runs loaders on initial client hydration. On the
    // client, reuse SSR loader data from window.__SSR_COLLECTIONS__ instead
    // of re-fetching. This ensures ZERO client /api/v1/collections on
    // direct pageload.
    if (typeof window !== 'undefined' && (window as any).__SSR_COLLECTIONS__) {
      const cached = (window as any).__SSR_COLLECTIONS__
      delete (window as any).__SSR_COLLECTIONS__
      if (cached.initialCollections !== undefined) {
        return cached
      }
    }

    // No-op when this route matches a path without an actual network slug
    // (e.g. /en/collections fires both index.tsx and $networkSlug.tsx loaders).
    // index.tsx owns the base route; don't duplicate its fetch here.
    if (networkSlug === undefined) {
      return { initialCollections: [], initialNetwork: 'any' }
    }

    // Numeric slug → use as-is for net param, redirect to chainName slug
    let numericChainId: string
    if (/^\d+$/.test(networkSlug)) {
      numericChainId = networkSlug
      const chainEntry = chainData.find((e) => e && e.chainId === networkSlug)
      if (chainEntry && chainEntry.chainName) {
        const slug = chainEntry.chainName.toLowerCase().replace(/\s+/g, '-')
        throw redirect({ href: `/$locale/collections/${slug}`, statusCode: 302 })
      }
      throw redirect({ href: '/404', statusCode: 404 })
    }
    // String slug → resolve to numeric chainId via chainName map
    numericChainId = chainNameSlugToChainIdMap[networkSlug.toLowerCase()]
    if (numericChainId === undefined) {
      throw redirect({ href: '/404', statusCode: 404 })
    }

    const searchParam = new URLSearchParams(location.search).get('q') ?? ''
    const initialNetwork = numericChainId
    const collectionsResult = await getCollections({ net: numericChainId, limit: 24, sparam: searchParam })
    const collections = collectionsResult.data || []
    const paginationData = collectionsResult.pagination || { hasNext: false, hasPrev: false, page: 1, totalPages: 0 }

    // Build breadcrumb JSON-LD
    const chainEntry = chainData.find((e: any) => String(e.chainId) === String(initialNetwork))
    const chainName = chainEntry?.chainName || initialNetwork

    const breadcrumbJsonLd = {
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      breadcrumbList: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://stealthisnft.xyz/" },
        { "@type": "ListItem", position: 2, name: "Collections", item: "https://stealthisnft.xyz/collections" },
        { "@type": "ListItem", position: 3, name: chainName || "Collections", item: `https://stealthisnft.xyz/collections/${chainEntry?.chainName.toLowerCase().replace(/\s+/g, '-') || initialNetwork}` },
      ],
    }

    const structuredData = collections?.length
      ? createItemListSchema({
          title: t("meta.collections.title"),
          description: t("meta.collections.description"),
          url: "https://stealthisnft.xyz/collections",
          items: collections.slice(0, 20).map((c) => ({
            name: c.title || c.name || "Collection",
            description: c.description || "",
            image: c.image || c.bannerImage || "",
            url: c.route_url || `https://stealthisnft.xyz/collection/${c.slug || c.id}`,
          })),
        })
      : breadcrumbJsonLd

    // Set metadata in loader so it's available for SSR
    setPageMetadata({
      title: chainName ? `${chainName} | Collections` : t("meta.collections.title"),
      description: t("meta.collections.description"),
      ogImage: "",
      ogType: "website",
      twitterCard: "summary_large_image",
      jsonLd: [structuredData, ...(initialNetwork !== "any" ? [breadcrumbJsonLd] : [])],
    })

    return { initialCollections: collections, initialPagination: paginationData, initialNetwork, initialSearch: searchParam }
  },
  component: Collections,
})

function Collections() {
  const { initialCollections, initialPagination, initialNetwork, initialSearch } = Route.useLoaderData() as {
    initialCollections: any[]
    initialPagination: any
    initialNetwork: string
    initialSearch: string
  }
  return <CollectionsContainer initialCollections={initialCollections} initialPagination={initialPagination} initialNetwork={initialNetwork} initialSearch={initialSearch} />
}